import { useEffect, useState } from 'react';
import { BookOpen, Car, Pencil, Plus, Power, PowerOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/i18n';
import { Button } from '@/components/Shared/Button';
import { Badge } from '@/components/Shared/Badge';
import { Loading } from '@/components/Shared/Loading';
import { EmptyState } from '@/components/Shared/EmptyState';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '@/components/Shared/Table';
import { useVehicleStore } from '@/store/useVehicleStore';
import type { VehicleDto } from '@/store/useVehicleStore';
import VehicleForm from '@/pages/Vehicles/VehicleForm';
import VehicleCheckBadges from '@/pages/Vehicles/VehicleCheckBadges';
import { formatKm } from '@/utils/distance';

/** Fahrzeuge des Betriebs — Mitarbeiter wählen beim Ein- und Ausstempeln eines davon (FACF-115). */
export default function VehiclesPage() {
  const { t } = useTranslation(['vehicles', 'common']);
  const { vehicles, loading, saving, error, fetchVehicles, updateVehicle } = useVehicleStore();
  const navigate = useNavigate();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleDto | null>(null);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  const openCreate = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const openEdit = (vehicle: VehicleDto) => {
    setEditing(vehicle);
    setIsFormOpen(true);
  };

  const toggleActive = (vehicle: VehicleDto) => {
    if (!vehicle.id) return;
    updateVehicle(vehicle.id, {
      description: vehicle.description,
      isActive: !vehicle.isActive,
    });
  };

  // Aktive zuerst — die inaktiven stehen nur noch zur Reaktivierung in der Liste.
  const rows = [...vehicles].sort((a, b) => Number(b.isActive) - Number(a.isActive));

  const createButton = (
    <Button
      onClick={openCreate}
      leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
      className="w-full sm:w-auto"
    >
      {t('vehicles:actions.create')}
    </Button>
  );

  const renderContent = () => {
    if (loading && vehicles.length === 0) {
      return (
        <div className="p-4">
          <Loading />
        </div>
      );
    }
    if (error) {
      return (
        <div className="p-4">
          <EmptyState variant="error" title={t(error)} onRetry={() => fetchVehicles()} />
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <div className="p-4">
          <EmptyState
            icon={<Car className="w-12 h-12" strokeWidth={1.75} />}
            title={t('vehicles:empty')}
            description={t('vehicles:emptyHint')}
            action={createButton}
          />
        </div>
      );
    }
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>{t('vehicles:fields.licensePlate')}</TableHeader>
            <TableHeader className="hidden sm:table-cell">
              {t('vehicles:fields.description')}
            </TableHeader>
            <TableHeader className="hidden md:table-cell text-right">
              {t('vehicles:fields.currentOdometer')}
            </TableHeader>
            <TableHeader className="hidden sm:table-cell">{t('vehicles:checks.title')}</TableHeader>
            <TableHeader>{t('vehicles:fields.status')}</TableHeader>
            <TableHeader className="text-right">{t('common:actions.title')}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((vehicle) => (
            <TableRow key={vehicle.id} className={vehicle.isActive ? '' : 'opacity-60'}>
              <TableCell>
                <div className="flex flex-col gap-0.5">
                  <span className="font-mono text-[13px] font-medium text-slate-900">
                    {vehicle.licensePlate}
                  </span>
                  {vehicle.description && (
                    <span className="text-[11px] text-slate-500 sm:hidden">
                      {vehicle.description}
                    </span>
                  )}
                  <div className="sm:hidden mt-1">
                    <VehicleCheckBadges checks={vehicle.currentChecks} />
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden sm:table-cell text-[13px] text-slate-600">
                {vehicle.description || '–'}
              </TableCell>
              <TableCell className="hidden md:table-cell text-right font-mono text-[13px] tabular-nums text-slate-700">
                {formatKm(vehicle.currentOdometerKm)}
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <VehicleCheckBadges checks={vehicle.currentChecks} />
              </TableCell>
              <TableCell>
                <Badge variant={vehicle.isActive ? 'success' : 'default'}>
                  {vehicle.isActive ? t('vehicles:status.active') : t('vehicles:status.inactive')}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1 sm:gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    title={t('vehicles:actions.openLogbook')}
                    aria-label={t('vehicles:actions.openLogbook')}
                    className="p-1 sm:p-2"
                    onClick={() => navigate(`/vehicles/logbook?vehicleId=${vehicle.id}`)}
                  >
                    <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={t('vehicles:actions.edit')}
                    aria-label={t('vehicles:actions.edit')}
                    className="p-1 sm:p-2"
                    onClick={() => openEdit(vehicle)}
                  >
                    <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={
                      vehicle.isActive
                        ? t('vehicles:actions.deactivate')
                        : t('vehicles:actions.reactivate')
                    }
                    aria-label={
                      vehicle.isActive
                        ? t('vehicles:actions.deactivate')
                        : t('vehicles:actions.reactivate')
                    }
                    className="p-1 sm:p-2"
                    disabled={saving}
                    onClick={() => toggleActive(vehicle)}
                  >
                    {vehicle.isActive ? (
                      <PowerOff className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" strokeWidth={2} />
                    ) : (
                      <Power className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" strokeWidth={2} />
                    )}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('vehicles:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">{t('vehicles:subtitle')}</p>
          </div>
          {createButton}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">{renderContent()}</div>

      <VehicleForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} vehicle={editing} />
    </div>
  );
}
